import { useQueryClient } from '@tanstack/react-query';

import {
  getApiV1SerievideoVideoidLangsQueryOptions,
  usePostApiV1SerievideoVideoidLangs,
  usePutApiV1SerievideoVideoidLangsId,
} from '@/shared/api/generated';
import type { SerieVideoLangFormValues } from '../types/serieVideoLangForm';

/** Callbacks do salvamento de Idioma do Vídeo. */
export interface SaveSerieVideoLangCallbacks {
  readonly onSuccess?: () => void;
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um Idioma do Vídeo.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST` e `PUT /api/v1/serievideo/{videoId}/langs[/{id}]`),
 * escolhendo a operação pela presença de `id` e invalidando o cache da
 * listagem no sucesso.
 *
 * @param videoId - ID do vídeo
 *
 * @example
 * ```tsx
 * const { save, isPending } = useSaveSerieVideoLang(videoId);
 * save(values, editingLangId, { onSuccess, onError });
 * ```
 */
export function useSaveSerieVideoLang(videoId: number) {
  const queryClient = useQueryClient();
  const createMutation = usePostApiV1SerievideoVideoidLangs();
  const updateMutation = usePutApiV1SerievideoVideoidLangsId();

  const invalidate = () =>
    queryClient.invalidateQueries(getApiV1SerievideoVideoidLangsQueryOptions(videoId));

  const save = (
    values: SerieVideoLangFormValues,
    id: number | undefined,
    callbacks?: SaveSerieVideoLangCallbacks,
  ) => {
    const data = {
      cultureInfo: values.cultureInfo,
      title: values.title,
      resumee: values.description,
      embed: values.channel,
    };
    const onSuccess = () => {
      void invalidate();
      callbacks?.onSuccess?.();
    };
    const onError = () => callbacks?.onError?.();

    if (id === undefined) {
      createMutation.mutate({ videoId, data }, { onSuccess, onError });
    } else {
      updateMutation.mutate({ videoId, id, data }, { onSuccess, onError });
    }
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
  } as const;
}
